import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Settings, Plus, Trash2, Edit2, Save, X, ChevronRight } from 'lucide-react';

const CATEGORY_LABELS = {
  quote_types: 'Quote Types', appraisal_statuses: 'Appraisal Statuses', general_actions: 'General Actions',
  general_statuses: 'General Statuses', roof_types: 'Roof Types', roof_brands: 'Roof Brands',
  roof_colours: 'Roof Colours', roof_profiles: 'Roof Profiles', roof_states: 'Roof States',
  roof_pitches: 'Roof Pitches', roof_ages: 'Roof Ages', component_states: 'Component States',
  sheet_types: 'Sheet Types', tile_types: 'Tile Types', ridge_types: 'Ridge Types',
  valley_types: 'Valley Types', barge_types: 'Barge Types', spouting_types: 'Spouting Types',
  fastening_types: 'Fastening Types', flashing_types: 'Flashing Types', lead_types: 'Lead Types',
  lead_sources: 'Lead Sources', acceptance_types: 'Acceptance Types', payment_types: 'Payment Types',
  delivery_types: 'Delivery Types', materials_suppliers: 'Materials Suppliers',
};

export default function DropdownManagerPage() {
  const { api } = useAuth();
  const [allDropdowns, setAllDropdowns] = useState({});
  const [activeCategory, setActiveCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [editIdx, setEditIdx] = useState(-1);
  const [editValue, setEditValue] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/dropdowns');
      setAllDropdowns(data);
      if (!activeCategory && Object.keys(data).length > 0) {
        setActiveCategory(Object.keys(data).sort()[0]);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, activeCategory]);

  useEffect(() => { load(); }, [load]);

  const options = allDropdowns[activeCategory] || [];
  const categories = Object.keys(allDropdowns).sort();

  const addOption = async () => {
    if (!newValue.trim()) return;
    try {
      const data = await api.post(`/dropdowns/${activeCategory}/add`, { value: newValue.trim() });
      setAllDropdowns(prev => ({ ...prev, [activeCategory]: data.data }));
      setNewValue('');
    } catch (e) { alert('Failed to add'); }
  };

  const removeOption = async (value) => {
    if (!window.confirm(`Remove "${value}" from ${CATEGORY_LABELS[activeCategory] || activeCategory}?`)) return;
    try {
      const data = await api.post(`/dropdowns/${activeCategory}/remove`, { value });
      setAllDropdowns(prev => ({ ...prev, [activeCategory]: data.data }));
    } catch (e) { alert('Failed to remove'); }
  };

  const saveEdit = async (oldValue) => {
    if (!editValue.trim() || editValue.trim() === oldValue) { setEditIdx(-1); return; }
    try {
      const newOptions = options.map((o, i) => i === editIdx ? editValue.trim() : o);
      await api.put(`/dropdowns/${activeCategory}`, { options: newOptions });
      setAllDropdowns(prev => ({ ...prev, [activeCategory]: newOptions }));
      setEditIdx(-1);
    } catch (e) { alert('Failed to update'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  return (
    <div data-testid="dropdown-manager-page" className="space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Dropdown Options</h1>
        <p className="text-sm text-slate-500 mt-1 font-medium">Manage values used across the CRM — quote types, roof attributes, statuses, and more. Changes apply immediately to all forms.</p>
      </div>

      <div className="flex gap-5">
        {/* Category Sidebar */}
        <div className="w-60 flex-shrink-0">
          <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden max-h-[75vh] overflow-y-auto thin-scroll">
            {categories.map(cat => (
              <button
                key={cat}
                data-testid={`dd-cat-${cat}`}
                onClick={() => { setActiveCategory(cat); setEditIdx(-1); setNewValue(''); }}
                className={`w-full text-left px-4 py-2.5 text-sm border-b border-[#E2E2DF] last:border-0 transition-colors flex items-center justify-between ${activeCategory === cat ? 'bg-[#D35400]/[0.08] text-[#D35400] nav-active-accent font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <span>{CATEGORY_LABELS[cat] || cat}</span>
                <span className="text-[10px] text-slate-400 font-mono-num font-semibold">{(allDropdowns[cat] || []).length}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Options Editor */}
        <div className="flex-1 space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{CATEGORY_LABELS[activeCategory] || activeCategory}</h2>
            <span className="text-xs text-slate-500 font-mono-num"><span className="font-bold text-slate-700">{options.length}</span> options</span>
          </div>

          {/* Add New */}
          <div className="flex gap-2">
            <input
              data-testid="dd-new-value"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addOption()}
              placeholder={`Add new ${CATEGORY_LABELS[activeCategory] || activeCategory} option...`}
              className="flex-1 px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
            />
            <button data-testid="dd-add-btn" onClick={addOption} disabled={!newValue.trim()} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000] disabled:opacity-50 shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
              <Plus size={14} strokeWidth={2.5} /> Add
            </button>
          </div>

          {/* Options List */}
          <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
            <div className="max-h-[60vh] overflow-y-auto thin-scroll">
              {options.map((opt, idx) => (
                <div key={`${opt}-${idx}`} className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E2E2DF] last:border-0 hover:bg-orange-50/30 group transition-colors">
                  {editIdx === idx ? (
                    <>
                      <input data-testid="dd-edit-input" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(opt)} className="flex-1 px-2 py-1 border border-[#D35400] rounded text-sm" autoFocus />
                      <button onClick={() => saveEdit(opt)} className="p-1 text-emerald-600 hover:text-emerald-700 transition-colors"><Save size={14} /></button>
                      <button onClick={() => setEditIdx(-1)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />
                      <span className="flex-1 text-sm text-slate-700">{opt}</span>
                      <button data-testid={`dd-edit-${idx}`} onClick={() => { setEditIdx(idx); setEditValue(opt); }} className="p-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-[#D35400] transition-all"><Edit2 size={13} /></button>
                      <button data-testid={`dd-delete-${idx}`} onClick={() => removeOption(opt)} className="p-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-rose-500 transition-all"><Trash2 size={13} /></button>
                    </>
                  )}
                </div>
              ))}
              {options.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">No options in this category</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
